import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowPathIcon,
  ChatBubbleBottomCenterTextIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import useTranslation from "../../hooks/useTranslation";

export const Route = createFileRoute("/app/generate_text_mockup")({
  component: GenerateTextMockupPage,
});

type SuggestionState = "idle" | "loading" | "ready";

type Scenario = {
  teaserEyebrowKey: string;
  teaserTitleKey: string;
  teaserBodyKey: string;
  teaserReasonKey: string;
  suggestionTopicKey: string;
  suggestionHookKey: string;
  blocks: string[];
  continuationBlocks: string[];
  vocabulary: {
    wordKey: string;
    translationKey: string;
    noteKey: string;
  }[];
};

const SCENARIOS: Scenario[] = [
  {
    teaserEyebrowKey: "generate_text_mockup_teaser_walk_eyebrow",
    teaserTitleKey: "generate_text_mockup_teaser_walk_title",
    teaserBodyKey: "generate_text_mockup_teaser_walk_body",
    teaserReasonKey: "generate_text_mockup_teaser_walk_reason",
    suggestionTopicKey: "generate_text_mockup_teaser_walk_topic",
    suggestionHookKey: "generate_text_mockup_teaser_walk_hook",
    blocks: [
      "generate_text_mockup_walk_block_1",
      "generate_text_mockup_walk_block_2",
      "generate_text_mockup_walk_block_3",
    ],
    continuationBlocks: [
      "generate_text_mockup_walk_block_4",
      "generate_text_mockup_walk_block_5",
    ],
    vocabulary: [
      {
        wordKey: "generate_text_mockup_walk_word_1",
        translationKey: "generate_text_mockup_walk_word_1_translation",
        noteKey: "generate_text_mockup_walk_word_1_note",
      },
      {
        wordKey: "generate_text_mockup_walk_word_2",
        translationKey: "generate_text_mockup_walk_word_2_translation",
        noteKey: "generate_text_mockup_walk_word_2_note",
      },
    ],
  },
  {
    teaserEyebrowKey: "generate_text_mockup_teaser_cafe_eyebrow",
    teaserTitleKey: "generate_text_mockup_teaser_cafe_title",
    teaserBodyKey: "generate_text_mockup_teaser_cafe_body",
    teaserReasonKey: "generate_text_mockup_teaser_cafe_reason",
    suggestionTopicKey: "generate_text_mockup_teaser_cafe_topic",
    suggestionHookKey: "generate_text_mockup_teaser_cafe_hook",
    blocks: [
      "generate_text_mockup_cafe_block_1",
      "generate_text_mockup_cafe_block_2",
      "generate_text_mockup_cafe_block_3",
    ],
    continuationBlocks: [
      "generate_text_mockup_cafe_block_4",
      "generate_text_mockup_cafe_block_5",
    ],
    vocabulary: [
      {
        wordKey: "generate_text_mockup_cafe_word_1",
        translationKey: "generate_text_mockup_cafe_word_1_translation",
        noteKey: "generate_text_mockup_cafe_word_1_note",
      },
      {
        wordKey: "generate_text_mockup_cafe_word_2",
        translationKey: "generate_text_mockup_cafe_word_2_translation",
        noteKey: "generate_text_mockup_cafe_word_2_note",
      },
    ],
  },
  {
    teaserEyebrowKey: "generate_text_mockup_teaser_train_eyebrow",
    teaserTitleKey: "generate_text_mockup_teaser_train_title",
    teaserBodyKey: "generate_text_mockup_teaser_train_body",
    teaserReasonKey: "generate_text_mockup_teaser_train_reason",
    suggestionTopicKey: "generate_text_mockup_teaser_train_topic",
    suggestionHookKey: "generate_text_mockup_teaser_train_hook",
    blocks: [
      "generate_text_mockup_train_block_1",
      "generate_text_mockup_train_block_2",
      "generate_text_mockup_train_block_3",
    ],
    continuationBlocks: [
      "generate_text_mockup_train_block_4",
      "generate_text_mockup_train_block_5",
    ],
    vocabulary: [
      {
        wordKey: "generate_text_mockup_train_word_1",
        translationKey: "generate_text_mockup_train_word_1_translation",
        noteKey: "generate_text_mockup_train_word_1_note",
      },
      {
        wordKey: "generate_text_mockup_train_word_2",
        translationKey: "generate_text_mockup_train_word_2_translation",
        noteKey: "generate_text_mockup_train_word_2_note",
      },
    ],
  },
];

const MOODS = [
  "generate_text_mockup_mood_gentle",
  "generate_text_mockup_mood_playful",
  "generate_text_mockup_mood_curious",
] as const;

function GenerateTextMockupPage() {
  const { translate } = useTranslation();
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [selectedMood, setSelectedMood] =
    useState<(typeof MOODS)[number]>("generate_text_mockup_mood_gentle");
  const [topicValue, setTopicValue] = useState("");
  const [suggestionState, setSuggestionState] =
    useState<SuggestionState>("idle");
  const [hasStartedReading, setHasStartedReading] = useState(false);
  const [visibleBlockCount, setVisibleBlockCount] = useState(0);
  const [selectedWordIndex, setSelectedWordIndex] = useState<number | null>(null);
  const timerRef = useRef<number | null>(null);

  const scenario = SCENARIOS[scenarioIndex];
  const allBlocks = [...scenario.blocks, ...scenario.continuationBlocks];
  const visibleBlocks = allBlocks.slice(0, visibleBlockCount);
  const selectedWord =
    selectedWordIndex === null ? null : scenario.vocabulary[selectedWordIndex];
  const canContinue = visibleBlockCount < allBlocks.length;

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  const resetForScenario = (nextIndex: number) => {
    setScenarioIndex(nextIndex);
    setSuggestionState("idle");
    setTopicValue("");
    setHasStartedReading(false);
    setVisibleBlockCount(0);
    setSelectedWordIndex(null);
  };

  const rotateScenario = () => {
    resetForScenario((scenarioIndex + 1) % SCENARIOS.length);
  };

  const handleMakeConcrete = () => {
    setSuggestionState("loading");
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      setTopicValue(translate(scenario.suggestionTopicKey));
      setSuggestionState("ready");
      timerRef.current = null;
    }, 550);
  };

  const startReading = () => {
    if (!topicValue.trim()) {
      setTopicValue(translate(scenario.suggestionTopicKey));
    }
    setHasStartedReading(true);
    setVisibleBlockCount(scenario.blocks.length);
    setSelectedWordIndex(0);
  };

  const handleContinue = () => {
    setVisibleBlockCount((current) => Math.min(current + 1, allBlocks.length));
  };

  const handleRegenerate = () => {
    const nextIndex = (scenarioIndex + 1) % SCENARIOS.length;
    resetForScenario(nextIndex);
    window.setTimeout(() => {
      setTopicValue(translate(SCENARIOS[nextIndex].suggestionTopicKey));
      setHasStartedReading(true);
      setVisibleBlockCount(SCENARIOS[nextIndex].blocks.length);
      setSelectedWordIndex(0);
    }, 0);
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-5 sm:px-6">
      <section className="rounded-[28px] border border-amber-200 bg-[linear-gradient(135deg,#fff7ed_0%,#fffbeb_45%,#f0f9ff_100%)] px-5 py-6 sm:px-7">
        <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
          {translate("generate_text_mockup_eyebrow")}
        </p>
        <h1 className="mt-3 max-w-4xl font-serif text-3xl text-slate-900 sm:text-4xl">
          {translate("generate_text_mockup_title")}
        </h1>
        <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-700">
          {translate("generate_text_mockup_subtitle")}
        </p>
      </section>

      <div className="mt-5 grid gap-5 xl:grid-cols-[1.02fr_1.18fr]">
        <section className="rounded-[28px] border border-slate-200 bg-white px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                {translate("generate_text_mockup_entry_label")}
              </p>
              <h2 className="mt-2 font-serif text-2xl text-slate-900">
                {translate("generate_text_mockup_entry_title")}
              </h2>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-700"
              onClick={rotateScenario}
            >
              <ArrowPathIcon className="h-4 w-4" />
              {translate("generate_text_mockup_rotate")}
            </button>
          </div>

          <div className="mt-5 rounded-[24px] border border-amber-200 bg-[linear-gradient(180deg,#fffaf0_0%,#ffffff_100%)] p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-amber-700">
                  {translate(scenario.teaserEyebrowKey)}
                </p>
                <h3 className="mt-2 font-serif text-2xl text-slate-900">
                  {translate(scenario.teaserTitleKey)}
                </h3>
              </div>
              <SparklesIcon className="mt-1 h-5 w-5 text-amber-700" />
            </div>
            <p className="mt-3 text-base leading-relaxed text-slate-700">
              {translate(scenario.teaserBodyKey)}
            </p>
            <p className="mt-3 rounded-[18px] border border-amber-100 bg-white/80 px-3 py-3 text-sm leading-relaxed text-slate-600">
              {translate(scenario.teaserReasonKey)}
            </p>
          </div>

          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
              {translate("generate_text_mockup_mood_label")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {MOODS.map((moodKey) => {
                const isActive = selectedMood === moodKey;
                return (
                  <button
                    key={moodKey}
                    type="button"
                    className={`rounded-full border px-3 py-2 text-sm ${
                      isActive
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-300 bg-white text-slate-700"
                    }`}
                    onClick={() => setSelectedMood(moodKey)}
                  >
                    {translate(moodKey)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 rounded-[22px] border border-slate-200 bg-slate-50/70 p-4">
            <label
              htmlFor="generate-text-mockup-topic"
              className="text-[11px] uppercase tracking-[0.18em] text-slate-500"
            >
              {translate("generate_text_mockup_topic_label")}
            </label>
            <div className="mt-3 flex items-center gap-3 rounded-[18px] border border-slate-300 bg-white px-4 py-3">
              <PencilSquareIcon className="h-5 w-5 text-slate-500" />
              <input
                id="generate-text-mockup-topic"
                value={topicValue}
                onChange={(event) => setTopicValue(event.target.value)}
                placeholder={translate("generate_text_mockup_topic_placeholder")}
                className="w-full border-0 bg-transparent p-0 text-base text-slate-900 outline-none placeholder:text-slate-400"
              />
            </div>

            <div className="mt-3 flex flex-wrap gap-3">
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-full border border-slate-900 bg-slate-900 px-4 py-3 text-sm font-medium text-white"
                onClick={startReading}
              >
                <SparklesIcon className="h-4 w-4" />
                {translate("generate_text_mockup_surprise_me")}
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700"
                onClick={handleMakeConcrete}
                disabled={suggestionState === "loading"}
              >
                <ChatBubbleBottomCenterTextIcon className="h-4 w-4" />
                {translate(
                  suggestionState === "loading"
                    ? "generate_text_mockup_make_concrete_loading"
                    : "generate_text_mockup_make_concrete",
                )}
              </button>
            </div>

            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {suggestionState === "ready"
                ? translate(scenario.suggestionHookKey)
                : translate("generate_text_mockup_entry_hint")}
            </p>
          </div>

          <div className="mt-4 rounded-[22px] border border-slate-200 bg-slate-50/70 p-4">
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
              {translate("generate_text_mockup_principles_label")}
            </p>
            <ul className="mt-3 space-y-3 text-sm leading-relaxed text-slate-700">
              <li>{translate("generate_text_mockup_principle_1")}</li>
              <li>{translate("generate_text_mockup_principle_2")}</li>
              <li>{translate("generate_text_mockup_principle_3")}</li>
            </ul>
          </div>
        </section>

        <section className="rounded-[28px] border border-slate-200 bg-[#fcfcfb] px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                {translate("generate_text_mockup_reader_label")}
              </p>
              <h2 className="mt-2 font-serif text-2xl text-slate-900">
                {translate("generate_text_mockup_reader_title")}
              </h2>
            </div>
            {hasStartedReading ? (
              <div className="rounded-full border border-slate-300 bg-white px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-600">
                {translate("generate_text_mockup_progress", {
                  current: String(visibleBlocks.length),
                  total: String(allBlocks.length),
                })}
              </div>
            ) : null}
          </div>

          {!hasStartedReading ? (
            <div className="mt-5 rounded-[24px] border border-dashed border-slate-300 bg-white px-5 py-8 text-center">
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                {translate("generate_text_mockup_reader_empty_label")}
              </p>
              <h3 className="mt-3 font-serif text-3xl text-slate-900">
                {translate("generate_text_mockup_reader_empty_title")}
              </h3>
              <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-slate-700">
                {translate("generate_text_mockup_reader_empty_body")}
              </p>
            </div>
          ) : (
            <div className="mt-5 rounded-[24px] border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                    {translate("generate_text_mockup_reader_mode")}
                  </p>
                  <p className="mt-1 text-base font-semibold text-slate-900">
                    {translate("generate_text_mockup_reader_mode_value")}
                  </p>
                </div>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-700"
                  onClick={handleRegenerate}
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  {translate("generate_text_mockup_regenerate")}
                </button>
              </div>

              <div className="mt-4 space-y-3">
                {visibleBlocks.map((blockKey) => (
                  <div
                    key={blockKey}
                    className="rounded-[20px] border border-slate-200 bg-[#fffdf8] px-4 py-4 text-lg leading-relaxed text-slate-900"
                  >
                    {translate(blockKey)}
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {scenario.vocabulary.map((item, index) => {
                  const isActive = index === selectedWordIndex;
                  return (
                    <button
                      key={item.wordKey}
                      type="button"
                      className={`rounded-full border px-3 py-2 text-sm ${
                        isActive
                          ? "border-sky-700 bg-sky-50 text-sky-900"
                          : "border-slate-300 bg-white text-slate-700"
                      }`}
                      onClick={() => setSelectedWordIndex(index)}
                    >
                      {translate(item.wordKey)}
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
                <div className="rounded-[20px] border border-slate-200 bg-slate-50/80 px-4 py-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                    {translate("generate_text_mockup_continue_label")}
                  </p>
                  <h3 className="mt-2 font-serif text-2xl text-slate-900">
                    {translate("generate_text_mockup_continue_title")}
                  </h3>
                  <p className="mt-2 text-base leading-relaxed text-slate-700">
                    {canContinue
                      ? translate("generate_text_mockup_continue_body")
                      : translate("generate_text_mockup_continue_done")}
                  </p>
                  <button
                    type="button"
                    className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-900 bg-slate-900 px-4 py-3 text-sm font-medium text-white disabled:border-slate-200 disabled:bg-slate-200 disabled:text-slate-500"
                    onClick={handleContinue}
                    disabled={!canContinue}
                  >
                    {translate("generate_text_mockup_continue")}
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>

                <div className="rounded-[20px] border border-slate-200 bg-white px-4 py-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                    {translate("generate_text_mockup_vocab_label")}
                  </p>
                  <h3 className="mt-2 font-serif text-2xl text-slate-900">
                    {selectedWord
                      ? translate(selectedWord.wordKey)
                      : translate("generate_text_mockup_vocab_empty_title")}
                  </h3>
                  <p className="mt-2 text-base leading-relaxed text-slate-700">
                    {selectedWord
                      ? translate(selectedWord.translationKey)
                      : translate("generate_text_mockup_vocab_empty_body")}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {selectedWord
                      ? translate(selectedWord.noteKey)
                      : translate("generate_text_mockup_vocab_empty_note")}
                  </p>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
